import React, { useCallback, useContext, useEffect } from 'react';

import { useTourDetailsContextQuery } from '~/queries/tours/useTourDetailsFormQueries';

import { Alert, Box, Button, CircularProgress, Divider, Stack } from '@mui/material';

import { Response } from '@luxuryescapes/contract-svc-tour';

import {
  SLIDING_SCALE_TAB_TITLE,
  SLIDING_SCALE_TAB_TYPE,
  isSlidingScaleEnabled,
} from './SlidingScale/isSlidingScaleEnabled';
import TourDetailsContext from './TourDetailsContext';
import TourDetailsLinksList from './TourDetailsLinksList';
import TourDetailsTabs from './TourDetailsTabs';

interface Props {
  tourId: string;
}

export default function TourDetailsNav({ tourId }: Props) {
  const { setTourDetails, setTabs, tabs, tourDetails } = useContext(TourDetailsContext);
  const { fetchContext, contextFetchReq } = useTourDetailsContextQuery();

  useEffect(() => {
    fetchContext(tourId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId]);

  useEffect(() => {
    switch (contextFetchReq.status) {
      case 'succeeded': {
        const { tabs, tourId, tourTitle, vendorId } = contextFetchReq.result;
        setTourDetails({ id: tourId, title: tourTitle, vendorId });
        // Tours moving form management from the backend to the frontend. This is tabbing is in an interim state.
        const tourItinerariesTab: Response.Tabs = {
          type: 'tour-itineraries',
          title: 'Tour itineraries',
        } as unknown as Response.Tabs;
        const newTabs = [...tabs];
        const optionsIndex = tabs.findIndex((tab) => tab.type === 'tour-options');
        if (optionsIndex !== -1) {
          newTabs.splice(optionsIndex + 1, 0, tourItinerariesTab);
        } else {
          newTabs.push(tourItinerariesTab);
        }
        // Injected client-side: the tour forms API does not serve this tab.
        if (isSlidingScaleEnabled()) {
          newTabs.push({
            type: SLIDING_SCALE_TAB_TYPE,
            title: SLIDING_SCALE_TAB_TITLE,
          } as unknown as Response.Tabs);
        }
        setTabs(newTabs);
      }
    }
  }, [contextFetchReq, setTabs, setTourDetails]);

  const handleRetry = useCallback(() => {
    fetchContext(tourId);
  }, [fetchContext, tourId]);

  return (
    <Stack direction="column" spacing={1}>
      {contextFetchReq.status === 'pending' && (
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress variant="indeterminate" />
        </Box>
      )}
      {contextFetchReq.status === 'succeeded' && (
        <>
          {!!tabs.length && <TourDetailsTabs tabs={tabs} />}
          <Divider variant="middle" />
          {!!tourDetails && (
            <TourDetailsLinksList
              tourId={tourDetails.id}
              tourTitle={tourDetails.title}
              vendorId={tourDetails.vendorId}
            />
          )}
        </>
      )}
      {contextFetchReq.status === 'failed' && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" variant="outlined" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          There was a problem fetching links!
        </Alert>
      )}
    </Stack>
  );
}
